import React, { ReactNode } from 'react'
import logger from '@adc/logger'
// @ts-ignore no types
import ms from 'ms'
import type { Maybe } from '@adc/sdk/types'
// @ts-ignore no types
import { hooks } from '@adc/ui-adc'
import _cx from 'classnames/bind'
import { useListingContext } from 'common/context/listing'
import * as CostCalculations from 'common/modules/bidBanner/costCalculations'
import ContractButton from 'common/modules/bidBanner/contractButton'
import {
  WinningBid,
  BuyersPremiumAndTotal,
} from 'common/modules/bidBanner/bidForm/costBreakdown'
import EarnestMoneyDeposit from 'common/modules/bidBanner/bidForm/earnestMoneyDeposit'
import * as BidDetails from 'common/modules/bidBanner/bidDetails'
import Header from 'common/modules/bidBanner/header'
import {
  Container,
  LeftMarginSection,
} from 'common/modules/bidBanner/container'
import { SimilarPropertiesButton } from 'common/modules/similarProperties'
import TimeInformation from '../timeInformation'
import CostBreakdown from '../costBreakdown'
import {
  LostReserveNotMetTooltip,
  SubjectToApprovalTooltip,
  SubjectToConfirmationTooltip,
} from '../tooltips'
import WinnerNextSteps from '../winnerNextSteps'
import hasSimilarProperties from '../../../utils/hasSimilarProperties'
// @ts-ignore no types
import { BidBannerProps } from '../index'
import ReserveMetRibbon from '../reserveRibbon'
import cls from './styles.scss'
import { BID_BANNER_STATES, getBidBannerState } from './common'
const cx = _cx.bind(cls)

const HEADER_WON = 'Congratulations! You won the auction.'
const HEADER_TEXTS = {
  NO_BIDS_MET_RESERVE: "The auction ended, but Reserve wasn't met.",
  AUCTION_ENDED: 'The auction ended.',
  USER_LOST_RESERVE_MET: 'Property sold. You are not the winning bidder.',
  USER_WON_SUBJECT_TO_APPROVAL: HEADER_WON,
  USER_WON_OUTRIGHT: HEADER_WON,
  USER_WON_SUBJECT_TO_CONFIRMATION: HEADER_WON,
  USER_WON_MANUALLY: 'Congratulations! You have the winning offer.',
  USER_CAN_SUBMIT_FINAL_OFFER:
    'For a limited time, only you can make an offer on this property.',
  USER_CAN_SUBMIT_FINAL_OFFER_SUBTEXT:
    'Your bid did not meet the reserve. Increase your chances of winning by making a more competitive offer with justifications.',
  FINAL_OFFER_PENDING: 'Your offer was submitted.',
  FINAL_OFFER_PENDING_SUBTEXT: 'Seller has 5 days to respond.',
}

const LISTING_UPDATE_FETCH_DELAY = ms('5s')

interface AfterProps extends BidBannerProps {
  bidDeposit: Maybe<number | undefined>
  similarProperties?: any[]
  refreshCurrentListing: () => void
}

export const After = (props: AfterProps) => {
  const { bidDeposit, similarProperties, refreshCurrentListing } = props

  const { segment, listing } = useListingContext()

  /**
   * NOTE: A better way to do this would be to store whether we had previously seen any bid banner _before_ the auction
   * ended. If we didn't then the server has likely given us the correct information before we initially rendered and
   * this is redundant. Since we don't track that now, the drawback to this will be a single extra request if a user
   * opens a PDP page where the segment has already ended.
   */
  hooks.useMountEffect(() => {
    setTimeout(() => {
      if (
        refreshCurrentListing &&
        typeof refreshCurrentListing === 'function'
      ) {
        refreshCurrentListing()
      } else {
        logger.error('refreshCurrentListing is not a function')
      }
    }, LISTING_UPDATE_FETCH_DELAY)
  })

  const renderAllCosts = () => {
    const winningBidAmount = segment?.result?.winning_bid_amount

    let calculations = null
    if (winningBidAmount) {
      calculations = listing.__methods__.getCostCalculations(winningBidAmount)
    }

    if (!calculations) return null

    return (
      <>
        <div className={cx('u-mb-3', 'u-mt-3')}>
          <WinningBid winningBidAmount={winningBidAmount} />
          <BuyersPremiumAndTotal
            listing={listing}
            bidAmount={winningBidAmount}
          />
          <EarnestMoneyDeposit
            className={cx('centered-section', 'u-mb-3')}
            listing={listing}
            earnestMoneyDeposit={calculations.earnestMoneyDeposit}
          />
        </div>
        <div className={cx('u-mb-3')}>
          <ContractButton />
        </div>
        <div className={cx('centered-section')}>
          <CostBreakdown
            bidAmount={winningBidAmount}
            isBidAmountValid
            segment={segment}
            listing={listing}
          />
        </div>
      </>
    )
  }

  const renderWinningBidOnly = () => {
    const winningBidAmount = segment?.result?.winning_bid_amount
    return (
      <>
        <div className={cx('u-mb-3', 'u-mt-3')}>
          <CostCalculations.YourWinningBid
            value={winningBidAmount}
            valueWeight='heavy'
          />
        </div>
        <div className={cx('u-mb-3')}>
          <ContractButton />
        </div>
      </>
    )
  }

  const renderWinnerCosts = () => {
    return listing.listing_configuration?.is_buyers_premium_available ||
      listing.listing_configuration?.is_earnest_money_deposit_available
      ? renderAllCosts()
      : renderWinningBidOnly()
  }

  const renderRightRibbon = () => {
    if (!segment) return null
    return <ReserveMetRibbon segment={segment} />
  }

  const renderUserWonManuallySubjectToConfirmation = () => {
    return (
      <Container
        ribbonColor='success'
        left={
          <>
            <Header
              containerClassName={cx('u-mb-3', 'u-mt-2')}
              color='success'
              subText={<SubjectToConfirmationTooltip />}
              icon={<Header.IconGavel />}
              text={HEADER_TEXTS.USER_WON_MANUALLY}
            />
            <LeftMarginSection>
              <WinnerNextSteps />
            </LeftMarginSection>
          </>
        }
        right={renderWinningBidOnly()}
      />
    )
  }

  const renderUserWonSubjectToConfirmation = () => {
    return (
      <Container
        ribbonColor='success'
        left={
          <>
            <Header
              containerClassName={cx('u-mb-3', 'u-mt-2')}
              color='success'
              subText={<SubjectToConfirmationTooltip />}
              icon={<Header.IconGavel />}
              text={HEADER_TEXTS.USER_WON_SUBJECT_TO_CONFIRMATION}
            />
            <LeftMarginSection>
              <WinnerNextSteps containerClassName={cx('u-mb-3')} />
              <BidDetails.After bidDeposit={bidDeposit} />
            </LeftMarginSection>
          </>
        }
        right={renderWinnerCosts()}
        rightRibbon={renderRightRibbon()}
      />
    )
  }

  const renderUserWonManuallyOutright = () => {
    return (
      <Container
        ribbonColor='success'
        left={
          <>
            <Header
              containerClassName={cx('u-mb-3', 'u-mt-2')}
              color='success'
              icon={<Header.IconGavel />}
              text={HEADER_TEXTS.USER_WON_MANUALLY}
            />
            <LeftMarginSection>
              <WinnerNextSteps />
            </LeftMarginSection>
          </>
        }
        right={renderWinningBidOnly()}
      />
    )
  }

  const renderUserWonOutright = () => {
    return (
      <Container
        ribbonColor='success'
        left={
          <>
            <Header
              containerClassName={cx('u-mb-3', 'u-mt-2')}
              color='success'
              icon={<Header.IconGavel />}
              text={HEADER_TEXTS.USER_WON_OUTRIGHT}
            />
            <LeftMarginSection>
              <WinnerNextSteps containerClassName={cx('u-mb-3')} />
              <BidDetails.After />
            </LeftMarginSection>
          </>
        }
        right={renderWinnerCosts()}
        rightRibbon={renderRightRibbon()}
      />
    )
  }

  const renderUserWonSubjectToApproval = () => {
    return (
      <Container
        ribbonColor='success'
        left={
          <>
            <Header
              containerClassName={cx('u-mb-3', 'u-mt-2')}
              color='success'
              icon={<Header.IconGavel />}
              subText={<SubjectToApprovalTooltip />}
              text={HEADER_TEXTS.USER_WON_SUBJECT_TO_APPROVAL}
            />
            <LeftMarginSection>
              <WinnerNextSteps containerClassName={cx('u-mb-3')} />
              <BidDetails.After />
            </LeftMarginSection>
          </>
        }
        right={renderWinnerCosts()}
        rightRibbon={renderRightRibbon()}
      />
    )
  }

  const renderSimilarPropertiesButtonIfAvailable = () => {
    return hasSimilarProperties(similarProperties) ? (
      <div className='u-mt-3'>
        {/* @ts-ignore next */}
        <SimilarPropertiesButton similarProperties={similarProperties} />
      </div>
    ) : null
  }

  const renderUserLost = (
    headerText: string,
    headerSubText: ReactNode = null
  ) => {
    return (
      <Container
        ribbonColor='gray'
        left={
          <>
            <div className={cx('u-mb-5')}>
              <TimeInformation segment={segment} />
            </div>
            <Header
              containerClassName={cx('u-mb-3')}
              color='gray'
              icon={<Header.IconGavel color='gray' />}
              subText={headerSubText}
              text={headerText}
            />
            <LeftMarginSection>
              <BidDetails.After />
            </LeftMarginSection>
          </>
        }
        right={renderSimilarPropertiesButtonIfAvailable()}
      />
    )
  }

  switch (getBidBannerState(segment, listing)) {
    case BID_BANNER_STATES.USER_WON_MANUALLY_SUBJECT_TO_CONFIRMATION:
      return renderUserWonManuallySubjectToConfirmation()
    case BID_BANNER_STATES.USER_WON_MANUALLY_OUTRIGHT:
      return renderUserWonManuallyOutright()
    case BID_BANNER_STATES.USER_WON_SUBJECT_TO_APPROVAL:
      return renderUserWonSubjectToApproval()
    case BID_BANNER_STATES.USER_WON_SUBJECT_TO_CONFIRMATION:
      return renderUserWonSubjectToConfirmation()
    case BID_BANNER_STATES.USER_WON_OUTRIGHT:
      return renderUserWonOutright()
    case BID_BANNER_STATES.USER_LOST_RESERVE_NOT_MET:
      return renderUserLost(
        HEADER_TEXTS.NO_BIDS_MET_RESERVE,
        <LostReserveNotMetTooltip />
      )
    case BID_BANNER_STATES.USER_LOST_RESERVE_MET:
      return renderUserLost(
        HEADER_TEXTS.AUCTION_ENDED,
        HEADER_TEXTS.USER_LOST_RESERVE_MET
      )
    default:
      return null
  }
}
